import { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { useParams } from "react-router";


import { useSnackbar } from "notistack";

import { makeStyles} from "@mui/styles";
import { Breadcrumbs, Button, CircularProgress, Divider, Link, Tooltip, Typography } from "@mui/material";
import CloudUpload from "@mui/icons-material/CloudUpload";

import { selectWells } from "../reducers/wellSlice";
import { selectLists } from "../reducers/listSlice";
import { selectUser } from "../reducers/userSlice";
import { addProject, selectProjects } from "../reducers/projectSlice";

import { NewList } from "../components/NewList";
import WellLists from "../components/WellLists";
import NewListSplitButton from "../components/NewListSplitButton";
import { PetroNinjaList } from "../components/PetroNinjaList";
import { FilterWellList } from "../components/FilterWellList";
import { selectOptions } from "../reducers/optionsSlice";
import { AccessTime, Sort } from "@mui/icons-material";
import { saveEconomics, selectEconomics } from "../reducers/economicsSlice";
import { selectFBA } from "../reducers/fbaSlice";
import { useSocket } from "../context/useSocket";


const useStyles = makeStyles(theme => ({
  root: {
    // backgroundColor: theme.palette.grey[100],
    border: 0,
    color: 'text.primary',
    height: '100%',
    width: 'calc(100% - 60px)',
    minWidth: 600,
    padding: '10px',
    paddingTop: '20px',
    overflow: 'auto',
    marginLeft: 60,
    marginTop: 40,
    marginBottom: 60,
    paddingBottom: 60,
  },
  icon: {
    color: theme.palette.text.secondary
  },
  projectButtons: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end'
  },
  breadCrumbs: {
    // marginTop: 15
  },
  status: {
    display: 'flex',
    justifyContent: 'space-between',
    width: 'calc(100%)',
    marginBottom: 0,
    alignItems: 'flex-end'
  },
  
}));

export const WellsContainer = () => {

  const params = useParams(); // get URL parameters
  
  // STATES
  const [newListOpen, setNewListOpen] = useState(false);
  const [pnListOpen, setPNListOpen] = useState(false);
  const [wellUwis, setWellUwis] = useState("");

  // REDUX
  const options = useSelector(selectOptions);
  const projects = useSelector(selectProjects);
  const currentProject = projects?.byId[projects?.currentProjectId];
  const lists = useSelector(selectLists);
  const list = lists?.byId?.[lists?.currentListId];
  const wells = useSelector(selectWells);
  const user = useSelector(selectUser);
  const economics = useSelector(selectEconomics);
  const fba = useSelector(selectFBA);

  const { socket } = useSocket();

  const [sortListsDesc, setSortListsDesc] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const demoUser = user?.value?.subscription === 'Demo';


  // SIDE EFFECTS
  useEffect(() => { // only runs once
    const wellUwis = params?.wellUwis || "";
    if (wellUwis.length > 0 && user?.value?.subscription === 'SSD') {
      setWellUwis(() => wellUwis);
      setNewListOpen(true);
    };
  },[]); // eslint-disable-line


  // STYLES
  const classes = useStyles();

  const projectId = useMemo(() => projects.currentProjectId || 'default', [projects.currentProjectId]);
  
  const listIds = useMemo(() => {
    let ids = [];
    if (projects.byId[projectId]?.listIds?.length > 0) {
      ids = [...projects.byId[projectId]?.listIds];
      if (sortListsDesc && ids?.length > 0) ids = ids.reverse();
    };
    return ids;
  }, [projects, sortListsDesc]); // eslint-disable-line

  const dispatch = useDispatch();

  const { enqueueSnackbar } = useSnackbar();

  if (document.title !== ('Wells | SSD')) document.title = ('Wells | SSD');

  if (user?.value?.subscription === 'SSD' && !user?.value?.forecastAccess) {
    return (
      <div style={{width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
        <Typography>Please add subscription to your account</Typography>
      </div>
    );
  } else {
    return (
      <div className={classes.root} style={{backgroundColor: user?.value?.darkMode ? '#121212' : '#f5f5f5'}}>
        {newListOpen && <NewList 
          handleNewListClose={handleNewListClose}
          projectId={projectId}
          userEmail={user?.value?.email}
          wellUwis={wellUwis}
        />}
        {pnListOpen && <PetroNinjaList 
          handlePNListClose={handlePNListClose}
          projectId={projectId}
          userEmail={user.value.email}
        />}
        <div style={{display: 'flex', flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between'}}>
          <div style={{display: 'flex', flexDirection: 'column'}}>
            <Typography color="textSecondary" style={{fontSize: 14}}>
              WELL MANAGEMENT
            </Typography>
            <Typography variant="h5">
              {projects.byId[projectId]?.name || ""}
            </Typography>
          </div>

          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
            <div style={{display: 'flex'}}>
              <div style={{position: 'relative'}}>
                <Tooltip title="Save project">
                  <span><Button 
                    startIcon={<CloudUpload/>} 
                    color="primary" 
                    variant="outlined" 
                    size="small" 
                    style={{marginRight: 10}} 
                    onClick={() => handleSaveProject(currentProject)}
                    disabled={demoUser || isLoading || user?.value?.email !== projects?.byId[projects?.currentProjectId]?.createdBy}
                  >
                    Save
                  </Button></span>
                </Tooltip>
                {isLoading && (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-11px',
                      marginLeft: '-12px',
                    }}
                  />
                )}
              </div>
              <NewListSplitButton handleNewListClick={handleNewListClick} handlePNListClick={handlePNListClick} demoUser={demoUser} />
            </div>
            <div style={{display: 'flex', alignItems: 'center', marginTop: 5}}>
              <AccessTime color="textSecondary" fontSize="14px" style={{ marginRight: 5 }} />
              <Typography color="textSecondary" fontSize="14px" textAlign={'right'}>Last saved: {new Date(currentProject?.lastModified)?.toLocaleString()}</Typography>
            </div>
          </div>
        </div>

        <div className={classes.projectButtons}>
          <Breadcrumbs className={classes.breadCrumbs}>
            <Link color="inherit" variant="body1" textDecoration="none" component={RouterLink} to="/">
              Projects
            </Link>
            <Typography variant="body1" color="textPrimary">{projects?.byId[projectId]?.name}</Typography>
          </Breadcrumbs>
        </div>

        <div className={classes.status}>
          <Typography fontSize={12} style={{marginBottom: 5}}>{`Total lists: ${currentProject?.listIds?.length || 0} | Sorted by: ${sortListsDesc ? 'newest' : 'oldest'}`}</Typography>
          <Button
            variant="text"
            size="small"
            color="primary"
            startIcon={<Sort />}
            onClick={e => setSortListsDesc(prev => !prev)}
          >
            Sort
          </Button>
        </div>

        <Divider /> 
        <WellLists projectId={projectId} lists={lists} listIds={listIds} wells={wells} />  

        {options?.showWellListFilter && <FilterWellList listId={options?.showWellListFilter} wells={list.wellUwis.map(uwi => wells.byUwi[uwi])} />}
      
      </div>
    );
  };

  
  // FUNCTIONS
  function handleNewListClick() {
    setNewListOpen(true);
  };

  function handlePNListClick() {
    setPNListOpen(true);
  };

  function handleNewListClose() {
    setNewListOpen(false);
  };  
  
  function handlePNListClose() {
    setPNListOpen(false);
  };  

  function handleSaveProject(project) {
    try {
      console.log('Saving project ' + project.name);

      setIsLoading(true);

      // save all project economics
      const economicsIds = economics?.allIds?.filter(id => economics?.byId?.[id]?.projectId === currentProject?.id); // only keep economicsIds that correspond to the current project
      if (economicsIds?.length > 0) {
        for (let economicsId of economicsIds) {
          if (economics?.byId?.[economicsId]?.forecastNet && economics?.byId?.[economicsId]?.cashflowNet && economics?.byId?.[economicsId]?.cashflowGross) {
            dispatch(saveEconomics(economics?.byId?.[economicsId]));
          };
        };
      };

      // save all project fbas
      const fbaIds = fba?.allIds?.filter(id => fba?.byId?.[id]?.projectId === currentProject?.id); // only keep economicsIds that correspond to the current project
      if (fbaIds?.length > 0 && socket && socket.readyState === 1) {
        for (let fbaId of fbaIds) {
          const currFBA = fba?.byId?.[fbaId];
          if (currFBA?.fbaData) {

            socket.send(JSON.stringify({
              messageType: 'saveFBA', 
              fba: currFBA, 
              fileName: currFBA?.id, 
              userEmail: user?.value?.email
            }));

          };
        };
      };

      let newWells = project?.wells?.length > 0 ? [...project.wells] : [];

      // compile a list of UWIs in the project
      let projectUwis = [];

      project.listIds.forEach(listId => {

        lists.byId[listId]?.wellUwis?.forEach(wellUwi => {
          projectUwis.push(wellUwi);
        });

      });

      for (let wellUwi of projectUwis) { // save local params
        try {
          if (wells?.byUwi?.[wellUwi]?.status === 'processed' && wells?.byUwi?.[wellUwi]?.params && !wells?.byUwi?.[wellUwi]?.local) { // if params are available, well is processed
            let wellParams = {...wells?.byUwi?.[wellUwi]?.params};
            const wellFluids = Object.keys(wellParams)?.filter(f => f !== 'cutoff');
    
            for (let fluid of wellFluids) {
              wellParams[fluid] = {...wellParams?.[fluid], outliers: []};
            };

            const wellIndex = newWells.map(w => w.uwi)?.indexOf(wellUwi);
            if (wellIndex === -1) { // if well hasnt been saved before, add it
              newWells.push({uwi: wellUwi, params: wellParams});
            } else { // update existing saved well with new params
              newWells.splice(wellIndex, 1, {uwi: wellUwi, params: wellParams});
            };
          };
        } catch (error) {
          console.log(error);
        };
      };

      const newProject = {
        id: project?.id,
        name: project?.name, 
        color: project?.color,
        description: project?.description || "",
        createdBy: project?.createdBy,
        lastModified: new Date().toLocaleString(),
        createdOn: project?.createdOn,
        listIds: project?.listIds || [],
        wells: newWells,
        currentListId: project?.currentListId || "",
        economicsIds: economicsIds?.length > 0 ? economicsIds : (project?.economicsIds || []),
        fbaIds: fbaIds?.length > 0 ? fbaIds : (project?.fbaIds || []),
        public: project?.public || false
      };

      const newLists = project?.listIds?.length > 0 ? project?.listIds?.map(listId => {
        const currentList = lists.byId[listId];
        return {
          id: listId,
          name: currentList?.name,
          createdBy: currentList?.createdBy,
          lastModified: new Date().toLocaleString(),
          color: currentList?.color,
          description: currentList?.description || "",
          wellUwis: currentList?.wellUwis,
          typeCurveId: currentList?.typeCurveId,
          groupWellId: currentList?.groupWellId,
          forecastListTab: currentList?.forecastListTab,
          filters: currentList?.filters
        };
      }) : [];

      dispatch(addProject({ project: newProject, lists: newLists, email: user?.value?.email })).then(() => {
        setIsLoading(false);
        enqueueSnackbar(`Project ${newProject?.name} saved successfully`, { variant: 'success' });
      });

    } catch (error) {
      setIsLoading(false);
      enqueueSnackbar(`Could not save project ${project?.name}`, { variant: 'error' });
      console.log(error);
    };
  };
};